import React from 'react';
import { Package, Boxes, DollarSign, Percent, AlertTriangle, Clock, TrendingUp } from 'lucide-react';

// Founder Executive Suite · web Inventory tab. Stock levels, sales-velocity
// countdowns (days-of-cover), per-SKU gross margins and restock alerts.
// Mirrors the mobile InventoryTab.
const C = {
  forest: '#1A4331', forestDeep: '#0F2D1F', cream: '#FAF6EC', creamWarm: '#F4EEDE',
  lime: '#B4D330', gold: '#C9A85B', leaf: '#6B9F4A', leafDeep: '#4E7A33', terracotta: '#C66B4A',
  text: '#2D241E', textMuted: '#5C665C', textSoft: '#7A6B57', white: '#FFFFFF', sageDeep: '#D9E1CB',
  border: 'rgba(26,67,49,0.10)', leafTint: 'rgba(107,159,74,0.14)',
};
const OF = { fontFamily: 'Outfit, sans-serif' };
const money0 = (n) => `$${Number(n || 0).toLocaleString('en-NZ', { maximumFractionDigits: 0 })}`;
const label = { fontSize: 11, fontWeight: 700, letterSpacing: '0.12em', color: C.textMuted, textTransform: 'uppercase' };

const STATUS = {
  critical: { bg: 'rgba(198,107,74,0.12)', fg: C.terracotta, label: 'CRITICAL' },
  low: { bg: 'rgba(201,168,91,0.16)', fg: '#8A6D2B', label: 'LOW' },
  ok: { bg: 'rgba(107,159,74,0.14)', fg: C.leafDeep, label: 'HEALTHY' },
};

export default function InventoryTab({ data }) {
  if (!data) return null;
  const s = data.summary || {};
  const items = data.items || [];
  const alert = data.top_alert;

  return (
    <div data-testid="inventory-tab">
      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 20 }}>
        <Tile Icon={Package} tone={C.forest} value={`${s.total_skus || 0}`} label="Tracked SKUs" />
        <Tile Icon={Boxes} tone={C.leafDeep} value={`${s.onhand_units || 0}`} label="On-hand units" />
        <Tile Icon={DollarSign} tone={C.gold} value={money0(s.onhand_retail_value)} label="Retail value" />
        <Tile Icon={Percent} tone={C.leaf} value={`${s.avg_margin_pct || 0}%`} label="Avg margin" />
        <Tile Icon={AlertTriangle} tone={C.terracotta} value={`${s.alerts_count || 0}`} label="Restock alerts" />
      </div>

      {/* Restock alert banner */}
      {alert && (
        <div style={{ display: 'flex', gap: 12, background: C.forestDeep, borderRadius: 18, padding: 20, marginBottom: 20, border: '1px solid rgba(198,107,74,0.3)' }} data-testid="inventory-alert">
          <div style={{ width: 44, height: 44, borderRadius: 12, display: 'grid', placeItems: 'center', background: 'rgba(198,107,74,0.18)', flexShrink: 0 }}>
            <AlertTriangle className="w-5 h-5" style={{ color: C.terracotta }} />
          </div>
          <div style={{ flex: 1 }}>
            <p style={{ ...OF, fontSize: 16, fontWeight: 700, color: C.cream, margin: 0 }}>Restock {alert.name}</p>
            <p style={{ fontSize: 13, color: 'rgba(250,246,236,0.75)', margin: '2px 0 0', lineHeight: 1.5 }}>{data.advice}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.forestDeep, background: C.lime, borderRadius: 999, padding: '4px 12px' }}>Suggested +{alert.suggested_qty} units</span>
              <span style={{ fontSize: 12, color: 'rgba(250,246,236,0.7)', background: 'rgba(250,246,236,0.08)', borderRadius: 999, padding: '4px 12px' }}>{alert.days_left}d of cover</span>
            </div>
          </div>
        </div>
      )}

      {/* Item table */}
      <p style={{ ...label, marginBottom: 10 }}>Stock by sales velocity</p>
      <div style={{ background: C.cream, borderRadius: 20, border: `1px solid ${C.border}`, overflow: 'hidden' }} data-testid="inventory-list">
        <div style={{ display: 'flex', gap: 12, padding: '12px 20px', background: 'rgba(26,67,49,0.05)' }}>
          <span style={{ flex: 1, ...label }}>Product</span>
          <span style={{ width: 90, textAlign: 'right', ...label }}>Stock</span>
          <span style={{ width: 90, textAlign: 'right', ...label }}>Velocity</span>
          <span style={{ width: 90, textAlign: 'right', ...label }}>Margin</span>
          <span style={{ width: 120, textAlign: 'right', ...label }}>Days left</span>
        </div>
        {items.length === 0 ? <div style={{ padding: 32, textAlign: 'center', color: C.textMuted }}>No physical stock tracked yet.</div> :
          items.map((it, i) => {
            const st = STATUS[it.status] || STATUS.ok;
            return (
              <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px', borderTop: `1px solid ${C.border}` }} data-testid={`inventory-item-${i}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600, color: C.text, margin: 0 }}>{it.name}</p>
                  <p style={{ fontSize: 12, color: C.textMuted, margin: 0 }}>{money0(it.price)} · {it.sold_30d} sold/30d · +{it.suggested_qty} suggested</p>
                </div>
                <span style={{ width: 90, textAlign: 'right', fontSize: 14, fontWeight: 600, color: C.text }}>{it.inventory}</span>
                <span style={{ width: 90, textAlign: 'right', fontSize: 13, color: C.textMuted }}>{it.weekly}/wk</span>
                <span style={{ width: 90, textAlign: 'right', fontSize: 14, fontWeight: 600, color: C.leafDeep }}>{it.margin_pct}%</span>
                <span style={{ width: 120, textAlign: 'right', display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}>
                  <Clock className="w-3.5 h-3.5" style={{ color: st.fg }} />
                  <span style={{ fontSize: 12, fontWeight: 700, color: st.fg, background: st.bg, borderRadius: 999, padding: '3px 9px' }}>{it.days_left}d</span>
                </span>
              </div>
            );
          })}
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <TrendingUp className="w-3.5 h-3.5 shrink-0" style={{ color: C.textMuted, marginTop: 2 }} />
        <span style={{ fontSize: 12, color: C.textMuted }}>Days-of-cover = on-hand ÷ blended sales velocity. Alerts fire below a {data.reorder_lead_days}-day reorder lead. Unit costs (COGS) are modeled estimates.</span>
      </div>
    </div>
  );
}

function Tile({ Icon, tone, value, label: lbl }) {
  return (
    <div style={{ background: C.cream, borderRadius: 14, border: `1px solid ${C.border}`, padding: 16 }}>
      <Icon className="w-4 h-4" style={{ color: tone }} />
      <p style={{ ...OF, fontSize: 22, fontWeight: 700, color: tone, margin: '4px 0 0' }}>{value}</p>
      <p style={{ fontSize: 12, color: C.textSoft, margin: 0 }}>{lbl}</p>
    </div>
  );
}
